import { useEffect, useRef, useState } from 'react';
import {
  FLOOR_PLANS,
  ROAD_DIRECTIONS,
  type FloorPlan,
  type PropertyType,
  type RoadDirection,
} from '../types';

export interface FormValues {
  propertyType: PropertyType;
  floorPlan: FloorPlan;
  buildingYear: number;
  // condo: 専有面積 / house: 土地面積
  area: number | undefined;
  // 延床面積 -- only set for house
  floorArea: number | undefined;
  // House-only optional inputs. undefined = user left blank, meaning the
  // model uses its NaN/empty branch (deliberately well-trained at 25%
  // train-time masking) instead of a hard-coded fallback.
  roadWidthM: number | undefined;
  roadDirection: RoadDirection | undefined;
}

interface Props {
  position: [number, number] | null;
  onSubmit: (values: FormValues) => void;
  loading: boolean;
  initialValues?: Partial<FormValues>;
  // When true, fires onSubmit once on mount as soon as a position is available.
  // Used to auto-restore a deep-linked prediction.
  autoSubmit?: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();

export default function PropertyForm({ position, onSubmit, loading, initialValues, autoSubmit }: Props) {
  const [propertyType, setPropertyType] = useState<PropertyType>(initialValues?.propertyType ?? 'condo');
  const [floorPlan, setFloorPlan] = useState<FloorPlan>(initialValues?.floorPlan ?? '2LDK');
  const [buildingYear, setBuildingYear] = useState<number>(initialValues?.buildingYear ?? CURRENT_YEAR - 10);
  const [area, setArea] = useState<string>(initialValues?.area != null ? String(initialValues.area) : '');
  const [floorArea, setFloorArea] = useState<string>(
    initialValues?.floorArea != null ? String(initialValues.floorArea) : '',
  );
  const [roadWidthM, setRoadWidthM] = useState<string>(
    initialValues?.roadWidthM != null ? String(initialValues.roadWidthM) : '',
  );
  const [roadDirection, setRoadDirection] = useState<RoadDirection | ''>(
    initialValues?.roadDirection ?? '',
  );
  // House-only optional inputs (延床面積 + 前面道路 幅員/方位) collapse
  // behind a single toggle row by default. Without this, switching to 戸建て
  // adds two extra rows and pushes the submit button below the fold on
  // phones with limited vertical real-estate. Auto-open when any of the
  // optional values is already populated (e.g. restored from a deep link)
  // so users see their values.
  const [houseAdvancedOpen, setHouseAdvancedOpen] = useState<boolean>(
    Boolean(initialValues?.floorArea ?? initialValues?.roadWidthM ?? initialValues?.roadDirection),
  );

  const canSubmit = position !== null && !loading;

  const buildValues = (): FormValues => ({
    propertyType,
    floorPlan,
    buildingYear,
    area: area === '' ? undefined : Number(area),
    floorArea: propertyType === 'house' && floorArea !== '' ? Number(floorArea) : undefined,
    roadWidthM:
      propertyType === 'house' && roadWidthM !== '' ? Number(roadWidthM) : undefined,
    roadDirection:
      propertyType === 'house' && roadDirection !== '' ? roadDirection : undefined,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(buildValues());
  };

  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (!autoSubmit || autoSubmittedRef.current) return;
    if (position == null || loading) return;
    autoSubmittedRef.current = true;
    onSubmit(buildValues());
    // buildValues closes over current state which is fine for one-shot init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, position, loading]);

  const areaLabel = propertyType === 'condo' ? '専有面積' : '土地面積';

  return (
    <form onSubmit={submit}>
      <h2 className="section-title">条件</h2>

      <fieldset className="mt-4">
        <legend className="field-label">種別</legend>
        <div className="grid grid-cols-2 gap-2">
          {(['condo', 'house'] as const).map((t) => (
            <label
              key={t}
              className={`cursor-pointer rounded py-3 text-center text-[13px] font-bold transition-colors ${
                propertyType === t
                  ? 'bg-primary text-sheet shadow'
                  : 'bg-sheet-sunk text-ink-2 hover:bg-primary-wash hover:text-primary-dark'
              }`}
            >
              <input
                type="radio"
                name="propertyType"
                value={t}
                checked={propertyType === t}
                onChange={() => setPropertyType(t)}
                className="sr-only"
              />
              {t === 'condo' ? '中古マンション' : '戸建て'}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="field-label">間取り</span>
          <select
            value={floorPlan}
            onChange={(e) => setFloorPlan(e.target.value as FloorPlan)}
            className="field"
          >
            {FLOOR_PLANS.map((fp) => (
              <option key={fp} value={fp}>{fp}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="field-label">建築年</span>
          <input
            type="number"
            min={1900}
            max={CURRENT_YEAR}
            value={buildingYear}
            onChange={(e) => setBuildingYear(Number(e.target.value))}
            className="field"
          />
        </label>
      </div>

      <label className="mt-5 block">
        <span className="field-label">
          {areaLabel} <span className="text-ink-3">㎡・任意</span>
        </span>
        <input
          type="number"
          min={5}
          max={1000}
          step={0.5}
          value={area}
          onChange={(e) => setArea(e.target.value)}
          placeholder="空欄なら間取りの中央値で推定します"
          className="field"
        />
      </label>

      {propertyType === 'house' && (
        <div className="mt-5 border-t border-rule pt-4">
          <button
            type="button"
            onClick={() => setHouseAdvancedOpen((v) => !v)}
            className="flex w-full items-baseline justify-between text-left text-[12px] font-bold text-primary-text hover:text-primary-dark"
            aria-expanded={houseAdvancedOpen}
            aria-controls="house-advanced"
          >
            <span>
              戸建ての追加条件 <span className="text-ink-3">任意・入れると精度が上がります</span>
            </span>
            <span className="ml-2 text-ink-3">{houseAdvancedOpen ? '閉じる' : '開く'}</span>
          </button>

          {houseAdvancedOpen && (
            <div id="house-advanced" className="mt-4 space-y-4">
              <label className="block">
                <span className="field-label">
                  延床面積 <span className="text-ink-3">㎡・任意</span>
                </span>
                <input
                  type="number"
                  min={5}
                  max={1000}
                  step={0.5}
                  value={floorArea}
                  onChange={(e) => setFloorArea(e.target.value)}
                  placeholder="空欄なら土地面積から推定します"
                  className="field"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="field-label">
                    前面道路 幅員 <span className="text-ink-3">m</span>
                  </span>
                  <input
                    type="number"
                    min={0.5}
                    max={50}
                    step={0.1}
                    value={roadWidthM}
                    onChange={(e) => setRoadWidthM(e.target.value)}
                    placeholder="任意"
                    className="field"
                  />
                </label>

                <label className="block">
                  <span className="field-label">前面道路 方位</span>
                  <select
                    value={roadDirection}
                    onChange={(e) => setRoadDirection(e.target.value as RoadDirection | '')}
                    className="field"
                  >
                    <option value="">指定しない</option>
                    {ROAD_DIRECTIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-6 w-full rounded bg-primary py-4 text-[15px] font-bold tracking-wide text-sheet shadow transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-sheet-sunk disabled:text-ink-3 disabled:shadow-none"
      >
        {loading ? '推定中' : '推定する'}
      </button>

      {!position && (
        <p className="mt-3 text-center text-[12px] tracking-copy text-ink-3">
          先に地図で地点を選んでください
        </p>
      )}
    </form>
  );
}
